/**
 * ListRelevantPipelines → the panel's `CandidateAccount[]` shape.
 *
 * The panel's table is two-level (account rows with opp children) because the
 * Rocky skill produces accounts. `ListRelevantPipelines` returns one FLAT row
 * per opportunity, so this groups by account to reuse that table — and with it
 * the selection, expansion, claim-building and Confirm path, unchanged.
 *
 * WHAT THE API DOES NOT PROVIDE. Three fields of `CandidateAccount` have no
 * source here, and are left empty rather than invented:
 *   - `industry` — not on the response.
 *   - `flags` — a skill-side heuristic.
 *   - `action` — likewise; the relevance judgment says why an opportunity
 *     matches, not what to do next.
 * The panel drops those columns when it is rendering this source, so the gaps
 * are not shown as blanks. `tags` is also absent: the default query excludes
 * already-tagged pairs, so every row here is by definition untagged.
 *
 * `score` CHANGES MEANING between the two sources — 1-5 relevance here, the
 * skill's own account score there. That is why the column is relabelled rather
 * than shared; a reader comparing the two would otherwise be comparing scales.
 *
 * RELEVANCE AND ITS REASON LIVE ON THE OPP ROWS ONLY. The judge scores
 * (opportunity x asset) pairs, which the backend collapses to the best asset per
 * OPPORTUNITY — there is no account-level judgment to report. Account rows
 * therefore carry no score and no reason, and the panel leaves both columns
 * blank on them. Rolling the max up to the account would read as a verdict on
 * the account, and its reason would be misattributed to every sibling
 * opportunity that did not earn it.
 */

import type { RelevantPipelineItem } from '@amzn/ivthub-client';
import type { CandidateAccount, CandidateOpp } from './types';

/** Reason `type` tag, so a cell renderer can tell relevance reasons apart. */
const RELEVANCE_REASON = 'relevance';

/**
 * `opptyId` and `relevanceScore` are `@required` in the model, but the TypeScript
 * codegen types every member as `T | undefined`, so the compiler cannot see that.
 * Both are load-bearing rather than cosmetic — the id is the table's trackBy key,
 * and an empty one makes Cloudscape collapse distinct rows into one; the score is
 * the sort key. So a row missing either is dropped rather than defaulted.
 */
type UsableItem = RelevantPipelineItem & { opptyId: string; relevanceScore: number };

function isUsable(item: RelevantPipelineItem): item is UsableItem {
  return typeof item.opptyId === 'string' && item.opptyId.length > 0
    && typeof item.relevanceScore === 'number';
}

function toOpp(item: UsableItem): CandidateOpp {
  const reason = item.scoreReason?.trim();
  return {
    opp_id: item.opptyId,
    opp_name: item.oppName ?? item.opptyId,
    // Nullable upstream (opportunity_cache has no NOT NULL on arr); the table
    // renders 0 as "—" rather than a misleading zero-dollar deal.
    arr: item.arr ?? 0,
    stage: item.sfdcStage ?? '',
    owner: item.oppOwner ?? '',
    // The model's own justification, carried through so Confirm persists it as
    // motion_opps.match_reason — the same slot the skill's reasons land in.
    reasons: reason ? [{ type: RELEVANCE_REASON, text: reason }] : [],
    tags: [],
    relevance_score: item.relevanceScore,
  };
}

/**
 * Group the flat rows into account rows, preserving the API's ordering.
 *
 * Order matters and is not re-sorted here: the backend returns relevance DESC,
 * then ARR DESC, then opportunity id. Accounts therefore appear in order of
 * their best opportunity, and re-sorting client-side would silently replace the
 * ranking this whole feature exists to provide.
 *
 * CALL THIS WITH EVERY PAGE LOADED SO FAR, not with one page. The sort is by
 * relevance, NOT by account, so one account's opportunities are scattered
 * through the result and routinely land on different pages. Grouping page by
 * page and concatenating would emit a second account row with the same
 * account_id — duplicate trackBy keys, which breaks selection and expansion.
 * Re-grouping the accumulated list keeps merging automatic and account order
 * stable, since a Map preserves insertion order and an account's first
 * appearance is its best-scoring opportunity.
 *
 * One consequence to surface in the UI rather than hide: an account's
 * `open_opps` (and so its opp count and ARR sum) is only complete once every
 * page is loaded. Those aggregates mean "as far as loaded", never "in total".
 */
export function toCandidateAccounts(items: readonly RelevantPipelineItem[]): CandidateAccount[] {
  const byKey = new Map<string, CandidateAccount>();
  // Guards against the same opportunity arriving twice. The keyset cursor should
  // not repeat a row, but a sync landing between two page requests can shift the
  // window, and a duplicate would double-count in the account's aggregates —
  // visible as an inflated opp count, with nothing to explain it.
  const seenOpps = new Set<string>();

  for (const item of items) {
    if (!isUsable(item)) continue;
    if (seenOpps.has(item.opptyId)) continue;
    seenOpps.add(item.opptyId);
    // accountId is nullable upstream. Fall back to the name, then to the
    // opportunity id, so a row is never dropped and never merged into a
    // different account's group by an empty key.
    const key = item.accountId || item.accountName || `oppty:${item.opptyId}`;
    const existing = byKey.get(key);
    const opp = toOpp(item);

    if (!existing) {
      byKey.set(key, {
        account_id: key,
        // `||` and not `??`, matching the key above: an empty accountName has to
        // fall through the same way it does there, or a row keyed on accountId
        // renders with a blank account name.
        account_name: item.accountName || key,
        industry: '',
        // Zero, and deliberately never read: the panel renders the Relevance
        // column from the OPP rows in this mode. An earlier version rolled the
        // max score up to the account, which invented an account-level verdict
        // the judge never produced — the grain is (opportunity x asset).
        score: 0,
        flags: [],
        open_opps: [opp],
        // Empty for the same reason. A relevance reason explains one
        // opportunity; attributing it to the account would misattribute it to
        // every other opportunity under that account.
        reasons: [],
        action: '',
        owner: undefined,
      });
      continue;
    }

    existing.open_opps.push(opp);
  }

  return [...byKey.values()];
}
